import { AlertTriangle, ArrowUpRight } from 'lucide-react';
import { startFunnelHref } from '@bookkeeper/shared/start-funnel';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation } from '@/i18n/i18n';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { PROOF_ANSWER } from '@/content/proof-answer/proof-answer.generated';
import { PROOF_ANSWER_COPY } from '@/content/proof-answer/copy';

const DATE_FORMAT: Record<'pl' | 'en' | 'uk', string> = {
  pl: 'pl-PL',
  en: 'en-GB',
  uk: 'uk-UA',
};

/**
 * "Dowody, nie obietnice" (JDG conversion design 2026-10-27, slice 2): one real answer from the
 * system rendered as live HTML in the page's own type, instead of small dark screenshots with
 * English UI strings. Rates, statute references and KIS signatures are generated from the tax
 * engine and the corpus (`src/content/proof-answer/`); the `/start` box sits directly under it.
 */
export const ProofStrip = () => {
  const sectionRef = useLandingSectionTracking('proof_strip');
  const { locale } = useTranslation();
  const copy = PROOF_ANSWER_COPY[locale];
  const leadRate = PROOF_ANSWER.rows[0].rate;
  const refusedRate = PROOF_ANSWER.rulings[0].refusedRate;
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(DATE_FORMAT[locale], { dateStyle: 'long', timeZone: 'UTC' }).format(
      new Date(`${iso}T00:00:00Z`)
    );

  return (
    <section
      ref={sectionRef}
      aria-labelledby="proof-strip-heading"
      className="px-5 sm:px-6 md:px-8 py-12 md:py-14 bg-background border-b border-border"
    >
      <div className="max-w-4xl mx-auto">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {copy.kicker}
        </p>
        <h2
          id="proof-strip-heading"
          className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight"
        >
          {copy.heading}
        </h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">{copy.subheading}</p>

        <article
          data-testid="proof-answer"
          className="mt-8 rounded-2xl border border-foreground/20 bg-card shadow-sm overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 sm:px-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-foreground">
              <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
              {copy.badge}
            </span>
            <span className="font-mono text-xs text-muted-foreground">PKD {PROOF_ANSWER.pkd}</span>
          </div>

          <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
            <p className="ml-auto w-fit max-w-[90%] rounded-2xl rounded-br-sm bg-muted px-4 py-2.5 text-sm sm:text-base">
              {copy.question}
            </p>

            <div className="space-y-2">
              <p className="text-lg font-semibold leading-snug">{copy.answerLead(leadRate)}</p>
              <p className="text-muted-foreground">{copy.answerBody}</p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {copy.columns.pkwiu}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {copy.columns.service}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium text-right">
                      {copy.columns.rate}
                    </th>
                    <th scope="col" className="hidden px-3 py-2 font-medium sm:table-cell">
                      {copy.columns.basis}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {PROOF_ANSWER.rows.map((row) => (
                    <tr key={row.pkwiu}>
                      <td className="px-3 py-2 font-mono tabular-nums">{row.pkwiu}</td>
                      <td className="px-3 py-2">{copy.rows[row.label]}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {locale === 'en' ? row.rate : row.rate.replace('.', ',')}%
                      </td>
                      <td className="hidden px-3 py-2 text-xs text-muted-foreground sm:table-cell">
                        {row.statuteRef}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              data-testid="proof-answer-warning"
              className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3"
            >
              <p className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="size-4 shrink-0 text-amber-600" aria-hidden="true" />
                {copy.warningTitle(refusedRate)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{copy.warningBody}</p>
              <ul className="mt-2 space-y-1 text-sm">
                {PROOF_ANSWER.rulings.map((ruling) => (
                  <li key={ruling.signature} className="flex flex-wrap items-baseline gap-x-2">
                    <a
                      href={ruling.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs text-foreground underline underline-offset-4 hover:no-underline"
                    >
                      {copy.rulingLink(ruling.signature)}
                      <ArrowUpRight className="size-3" aria-hidden="true" />
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {copy.rulingIssued(formatDate(ruling.issuedOn))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-muted-foreground">{copy.source}</p>
          </div>
        </article>

        <StartBox />
      </div>
    </section>
  );
};

/** The plan CTA directly under the answer: `/start` once live, the JDG sign-up until then. */
const StartBox = () => {
  const { locale, t } = useTranslation();
  const copy = PROOF_ANSWER_COPY[locale];
  const startHref = startFunnelHref('home_proof');
  const signupUrl = useSignupUrl({ entityType: 'jdg', source: 'home_proof' });
  const kind = startHref ? 'start' : 'signup';
  const label = startHref ? copy.startCta : t('hero.ctaPrimary');

  return (
    <div
      data-testid="proof-start-box"
      className="mt-6 flex flex-col gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h3 className="text-lg font-semibold">{copy.startHeading}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{copy.startBody}</p>
        <p className="mt-2 text-xs text-muted-foreground">{t('hero.priceLine')}</p>
      </div>
      <a
        href={startHref ?? signupUrl}
        data-testid="proof-start-cta"
        data-target-kind={kind}
        onClick={() => trackLandingCtaClicked('home_proof', label, { target: kind })}
        className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-xl bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:scale-[1.02]"
      >
        {label}
      </a>
    </div>
  );
};
